if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.cart = document.querySelector('mini-cart');
        this.submitButton = this.querySelector('[type="submit"]');
        this.priceOnRequest = this.dataset.priceOnRequest === 'true' || this.submitButton?.dataset.priceOnRequest === 'true';
        this.sectionId = this.dataset.sectionId || this.closest('product-info')?.dataset.section;
        this.submitButtonText = this.submitButton
          ? this.submitButton.querySelector('[data-add-to-cart-text]') || this.submitButton.querySelector('span') || this.submitButton
          : null;
        this.submitButtonPrice = this.submitButton?.querySelector('[data-add-to-cart-price]') || null;

        if (this.form) {
          if (this.variantIdInput) this.variantIdInput.disabled = this.priceOnRequest;
          this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        }

        if (this.submitButton) {
          this.ensureSubmitButtonPrice();
          if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (this.sectionId && event.data.sectionId !== this.sectionId) return;
          this.updateSubmitButtonPrice(event.data.variant);
        });

        const selectedVariant = this.closest('product-info')?.querySelector('variant-selects [data-selected-variant]')?.textContent;
        if (selectedVariant) {
          try {
            this.updateSubmitButtonPrice(JSON.parse(selectedVariant));
          } catch (error) {
            console.warn('Unable to parse selected variant for add-to-cart price.', error);
          }
        }
      }

      disconnectedCallback() {
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.priceOnRequest || !this.submitButton) return;
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
        if (!this.blockSubmitIfRequiredFieldsEmpty(evt)) return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        //this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${theme.routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = theme.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cart: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            //this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      blockSubmitIfRequiredFieldsEmpty(event) {
        if (!(this.form instanceof HTMLFormElement)) {
          throw new Error("Expected a form element.");
        }

        const formId = this.form.getAttribute('id');
        const requiredFields = document.querySelectorAll('[required]');
        let allFilled = true;
        for (const field of requiredFields) {
          const belongsToForm = (field.getAttribute('form') === formId || field.closest('form')?.id === formId);
          if (!belongsToForm) continue;
          
          let isValid = true;
          switch (field.type) {
            case 'checkbox':
            case 'radio':
              const group = document.querySelectorAll(`input[name="${field.name}"][type="${field.type}"]`);
              const isChecked = Array.from(group).some(input => input.checked);
              isValid = isChecked;
              break;
            case 'select-one':
              isValid = field.value !== '';
              break;
            case 'textarea':
            case 'text':
            case 'email':
            case 'number':
            case 'url':
            default:
              isValid = field.value.trim() !== '';
              break;
          }
          if (!isValid) {
            allFilled = false;
            field.classList.add('invalid');
          } else {
            field.classList.remove('invalid');
          }
        }
    
        if (!allFilled) {
          event.preventDefault();
          this.handleErrorMessage(theme.accessibilityStrings.fillInAllLineItemPropertyRequiredFields);
          return false;
        }
        return true;
      }
      
      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton) return;
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text && this.submitButtonText) this.submitButtonText.textContent = text;
          if (this.submitButtonPrice) this.submitButtonPrice.textContent = '';
        } else {
          this.submitButton.removeAttribute('disabled');
          if (this.submitButtonText) {
            this.submitButtonText.textContent = this.submitButton.dataset.preOrder === 'true' ? theme.variantStrings.preOrder : theme.variantStrings.addToCart;
          }
        }
      }

      setPriceOnRequest(enabled) {
        this.priceOnRequest = enabled;
        this.toggleAttribute('data-price-on-request', enabled);
        if (this.variantIdInput) this.variantIdInput.disabled = enabled;
        if (!this.submitButton) return;
        this.submitButton.toggleAttribute('hidden', enabled);
        this.submitButton.toggleAttribute('disabled', enabled);
        this.submitButton.toggleAttribute('data-price-on-request', enabled);
        if (enabled && this.submitButtonPrice) {
          this.submitButtonPrice.textContent = '';
        } else if (!enabled) {
          this.ensureSubmitButtonPrice();
        }
      }

      ensureSubmitButtonPrice() {
        if (this.priceOnRequest || !this.submitButton || this.submitButtonPrice || this.submitButtonText === this.submitButton) return;
        this.submitButtonPrice = document.createElement('span');
        this.submitButtonPrice.className = 'product-form__submit-price';
        this.submitButtonPrice.setAttribute('data-add-to-cart-price', '');
        this.submitButtonText.insertAdjacentElement('afterend', this.submitButtonPrice);
      }

      updateSubmitButtonPrice(variant) {
        const onRequest = !variant || variant.price <= 0;
        this.setPriceOnRequest(onRequest);
        if (onRequest || !this.submitButtonPrice) return;
        this.submitButtonPrice.textContent = variant.available
          ? theme.Currency.formatMoney(variant.price, theme.shopSettings.moneyFormat)
          : '';
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
